/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  findListingEntity,
  getListingProductIdFromState,
} from './helpers/entityReadHelpers';
import { upsertListing } from './helpers/entityAdapter';

export const pullProductFromMaster = createAsyncThunk(
  'products/pullProductFromMaster',
  async ({ productId, channelIds }, { getState, extra: { sdk } }) => {
    const state = getState();
    const masterProduct = state.products.items.find(({ _id }) => _id === productId);

    if (!masterProduct) {
      throw new Error('Master product not found in state');
    }

    const masterData = {
      title: masterProduct.title,
      sku: masterProduct.sku,
      description: masterProduct.description,
      descriptionHtml: masterProduct.descriptionHtml,
      vendor: masterProduct.vendor,
      productType: masterProduct.productType,
      handle: masterProduct.handle,
      category: masterProduct.category,
      meta: masterProduct.meta,
      availableOptions: masterProduct.availableOptions,
      status: masterProduct.status,
      taxable: masterProduct.taxable,
      tracked: masterProduct.tracked,
      requiresShipping: masterProduct.requiresShipping,
      countryCodeOfOrigin: masterProduct.countryCodeOfOrigin,
      harmonizedSystemCode: masterProduct.harmonizedSystemCode,
    };

    const results = await Promise.all(
      channelIds.map(async (channelId) => {
        const listingProductId = getListingProductIdFromState(state.products, productId, channelId);

        if (!listingProductId) {
          throw new Error(`Staging product not found for channel ${channelId}`);
        }

        const { data } = await sdk.products.updateListing(listingProductId, masterData);
        return { channelId, updatedListing: data };
      }),
    );

    return { productId, channelIds, results };
  },
);

export const reducer = (builder) => {
  builder
    .addCase(pullProductFromMaster.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(pullProductFromMaster.fulfilled, (state, { payload }) => {
      const { productId, results } = payload;
      state.isBusy = false;

      results.forEach(({ channelId, updatedListing }) => {
        const existingListing = findListingEntity(state, { masterProductId: productId, channelId });
        if (!existingListing) return;

        upsertListing(state, {
          ...existingListing,
          ...updatedListing,
          masterProductId: productId,
        });
      });
    })
    .addCase(pullProductFromMaster.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot pull from master';
    });
};
